import React, { useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import get from "lodash-es/get";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";

// Actions
import { deleteCase, getCaseDetail } from "../../state/caseDetail/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Icons
import shareIcon from "../../assets/icons/share.svg";
import deleteIcon from "../../assets/icons/delete-pink.svg";

// Config
import { getRoute, urls } from "../../config/routes";
import { snackbarTypes } from "../../config/snackbar";
import customEvents from "../../config/customEvents";
import { useNewCaseFlow } from "../../context/NewCaseFlowProvider";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useShowSteps from "../../hooks/useShowSteps";
import useTranslate from "../../hooks/useTranslate";
import isCaseLocked from "../../utils/isCaseLocked";

// Components
import Button from "../Button";
import ConfirmationButton from "../ConfirmationButton";
import PinPasscodeDialog from "../PinPasscodeDialog";
import NewIncidentButton from "../NewIncidentButton";

const useStyles = makeStyles(theme => ({
  button: {
    borderRadius: 25,
    padding: theme.spacing(0, 4),
    minHeight: 50,
    minWidth: 195
  },
  iconButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    minHeight: 50,
    borderRadius: 25
  },
  shareIcon: {
    marginRight: theme.spacing(1),
    height: 16,
    transform: "translateY(-2px)"
  },
  intelButton: {
    background:
      "transparent linear-gradient(72deg, #452CFC 0%, #23167E 100%) 0% 0% no-repeat padding-box"
  }
}));

const ActionButtons = ({ isCreating, showCaseOptions }) => {
  const [showPasscode, setShowPasscode] = useState(false);
  const { data, isLoading, isDeleting } = useSelector(s => s.caseDetail);
  const { trackException, trackEvent } = useAppInsights();
  const { userDetails } = useSelector(s => s.user);
  const classes = useStyles();
  const dispatch = useDispatch();
  const getText = useTranslate();
  const history = useHistory();
  const match = useRouteMatch(urls.caseDetail);
  const { open: openNewCaseFlow } = useNewCaseFlow();

  const caseId = get(match, "params.caseId");
  const showSteps = useShowSteps(caseId);

  const goTo = url => () => history.push(url);

  const handleDeleteCase = () => {
    setShowPasscode(false);
    trackEvent(customEvents.DELETE_CASE);
    dispatch(
      deleteCase({
        caseId,
        onSuccess: () => history.push(urls.cases),
        onFail: () => {
          trackException(`Failed to delete case: ${caseId}`);
          // Refetches in case the rejection happened because the case
          // became locked in the meantime, so the UI picks up the current
          // state.
          dispatch(getCaseDetail({ caseId, isUpdate: true }));
          dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
        }
      })
    );
  };

  const handleCreateCase = () => {
    trackEvent(customEvents.ENTER_CREATE_CASE);
    openNewCaseFlow();
  };

  const handleConfirmCreation = () => {
    trackEvent(customEvents.CREATE_CASE);
    history.push(urls.cases);
  };

  if (isCreating) {
    return (
      <>
        <ConfirmationButton
          className={classes.button}
          disableElevation
          onClick={handleConfirmCreation}
          variant="outlined"
        >
          {getText("header.actionButtons.cancelButton")}
        </ConfirmationButton>
      </>
    );
  }

  if (!showCaseOptions) return <NewIncidentButton onClick={handleCreateCase} />;

  if (isLoading || !data || !userDetails) {
    return (
      <>
        <Skeleton variant="rect" width={190} height={48} />
        <NewIncidentButton onClick={handleCreateCase} />
      </>
    );
  }

  const { isOwnedByUser } = data;
  const showCaseButtons = !showSteps;
  const showDeleteSubmit = !isCaseLocked(data) && isOwnedByUser;

  const togglePasscode = value => () => setShowPasscode(value);

  return (
    <>
      {showDeleteSubmit && (
        <ConfirmationButton
          color="secondary"
          disableElevation
          onClick={togglePasscode(true)}
          variant="outlined"
          className={classes.iconButton}
          dialog={{
            texts: [
              { key: 1, text: getText("deleteCaseDialog.description") },
              { key: 2, text: getText("deleteCaseDialog.title") }
            ],
            isLoading: !!isDeleting
          }}
        >
          <img src={deleteIcon} alt="" />
        </ConfirmationButton>
      )}
      {showCaseButtons && (
        <>
          <Button
            disableElevation
            onClick={goTo(getRoute(urls.shareWith, { caseId: data.id }))}
            variant="contained"
            color="primary"
            className={clsx(classes.button, {
              [classes.intelButton]: data.isIntel
            })}
          >
            <img src={shareIcon} alt="" className={classes.shareIcon} />
            {getText("common.prompts.shareWith")}
          </Button>
        </>
      )}
      <NewIncidentButton onClick={handleCreateCase} />
      <PinPasscodeDialog
        onClose={togglePasscode(false)}
        onSubmit={handleDeleteCase}
        open={showPasscode}
      />
    </>
  );
};

ActionButtons.propTypes = {
  isCreating: PropTypes.bool.isRequired,
  showCaseOptions: PropTypes.bool.isRequired
};

export default ActionButtons;
